import * as React from 'react'
import { Checkbox as CheckboxPrimitive } from 'radix-ui'

import { CheckOutlined, MinusOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Checkbox` primitive, from the new-york registry via the CLI
 * (`npx shadcn@latest add checkbox`). Backs the `src/antd/Checkbox` seam's
 * shadcn half (`src/antd/Checkbox.shadcn.tsx`); nothing else should import it,
 * and nothing reaches it until someone flips the design toggle.
 *
 * FOUR EDITS TO THE REGISTRY OUTPUT, each forced by this branch:
 *
 * 1. THE GLYPHS COME FROM THE ICON SEAM, not from `lucide-react`.
 *    `components.json` is the reference branch's copy and says
 *    `iconLibrary: lucide`, so the CLI wrote `import { CheckIcon } from
 *    'lucide-react'`. Outside `src/icons/antd-compat` a raw lucide import
 *    renders lucide with the design toggle OFF, which AGENTS.md forbids and
 *    `src/antd/__tests__/seam-adoption.evidence.test.tsx` counts. The seam's
 *    `CheckOutlined` maps to lucide's `Check`, which is the glyph the registry
 *    asked for.
 *
 * 2. SIZE IS `text-*`, NOT `size-*` — the icon seam keeps antd's convention of
 *    a `1em` svg, so a glyph takes its size from `font-size`. `text-sm` IS the
 *    registry's `size-3.5`. `src/components/ui/spinner.tsx` says more.
 *
 * 3. forwardRef. The registry writes plain function components (React 19's
 *    ref-as-prop); this repo is on the React 18 runtime with `@types/react`
 *    pinned to 17, so a ref handed to one is dropped with a console warning
 *    and the seam's imperative handle would be empty.
 *
 * 4. INDETERMINATE DRAWS A DASH. The registry's indicator renders the check
 *    unconditionally, so `checked="indeterminate"` — which is how antd's
 *    `indeterminate` prop reaches Radix — would draw a TICK on a box that
 *    means "some, not all". That is not a restyle, it is the wrong statement:
 *    antd draws a dash there, so this does too, and it is not filled, so the
 *    dash carries the primary colour itself.
 * ------------------------------------------------------------------------- */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentProps<typeof CheckboxPrimitive.Root>
>(({ className, checked, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      checked={checked}
      className={cn(
        'peer size-4 shrink-0 rounded-[4px] border border-input shadow-xs transition-shadow outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary dark:bg-input/30 dark:aria-invalid:ring-destructive/40 dark:data-[state=checked]:bg-primary',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        {checked === 'indeterminate' ? (
          <MinusOutlined className="text-sm" aria-hidden />
        ) : (
          <CheckOutlined className="text-sm" aria-hidden />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})
Checkbox.displayName = 'Checkbox'

export { Checkbox }
